import { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
} from "@mui/material";
import { toast } from "react-toastify";

import { cancelDischargeProcess } from "../../api/dischargeProcessApi";

// Client-side, UX-only check for "is the current user the admitting doctor".
// The backend is the real authorization boundary — this only decides whether
// to show a heads-up; fail-open (allow) whenever admittingDoctorId isn't
// supplied, mirroring bedDashboardRoles.js's / DischargeStageTracker.js's
// fail-open convention.
function isAdmittingDoctor(admittingDoctorId) {
  if (!admittingDoctorId) return true;
  const doctorId = sessionStorage.getItem("doctor_id");
  if (!doctorId) return true;
  return String(doctorId) === String(admittingDoctorId);
}

const CancelDischargeModal = ({
  open,
  onClose,
  dischargeProcessId,
  admittingDoctorId,
  onCancelled,
}) => {
  const [remarks, setRemarks] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const allowedByRole = isAdmittingDoctor(admittingDoctorId);

  const handleClose = () => {
    setRemarks("");
    onClose();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!remarks.trim()) return;
    if (!dischargeProcessId) {
      toast.error("Missing discharge process — cannot cancel.");
      return;
    }
    setSubmitting(true);
    try {
      const data = await cancelDischargeProcess(dischargeProcessId, remarks.trim());
      if (data?.status === "Success") {
        toast.success(data.message || "Discharge process cancelled.");
        setRemarks("");
        if (onCancelled) onCancelled(data.data);
        onClose();
      } else {
        toast.error(data?.message || "Could not cancel discharge process.");
      }
    } catch (err) {
      console.error("Cancel discharge process failed:", err?.response?.data || err?.message);
      toast.error(err?.response?.data?.message || "Could not cancel discharge process.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 600 }}>Cancel Discharge</DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {!allowedByRole && (
              <Typography variant="body2" color="warning.main">
                Only the admitting doctor for this admission can cancel this
                discharge process. You can still submit — the server will
                confirm.
              </Typography>
            )}
            <Box>
              <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500 }}>
                Reason for Cancellation *
              </Typography>
              <TextField
                fullWidth
                size="small"
                multiline
                rows={3}
                required
                placeholder="Describe why this discharge process is being cancelled"
                value={remarks}
                onChange={(e) => setRemarks(e.target.value)}
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleClose} variant="outlined" sx={{ mr: 1 }} disabled={submitting}>
            Close
          </Button>
          <Button type="submit" variant="contained" color="error" disabled={submitting}>
            {submitting ? "Cancelling..." : "Confirm Cancellation"}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default CancelDischargeModal;
